import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CreateObject, PostObjectResponse } from '../models/metadata';

// Define the updated entity type that includes custom_milestone
export type EntityType = 'publication' | 'activity' | 'milestone' | 'custom_milestone' | 'vvpm_activity';

export interface StrategicPillar {
  id: number;
  title: string;
  description: string;
  keywords: string[];
  color_code: string;
  created_at: string;
  updated_at: string;
}

// Create a class that extends CreateObject like UserRaw, Activity, CustomMilestone
export class StrategicPillarRaw extends CreateObject {
  id: number = 0;
  title: string = '';
  description: string = '';
  keywords: string[] = [];
  color_code: string = '#FF5733';
  created_at: string = '';
  updated_at: string = '';

  constructor(data?: Partial<StrategicPillar>) {
    super();
    if (data) {
      Object.assign(this, data);
    }
  }

  get abstractID(): string {
    return this.id.toString();
  }

  get abstractDate(): string {
    return this.updated_at || this.created_at || new Date().toISOString();
  }

  toPost(changes: StrategicPillarRaw): any {
    return {
      title: changes.title,
      description: changes.description,
      keywords: changes.keywords,
      color_code: changes.color_code
    };
  }

  static override parseFromPostResponse(post: PostObjectResponse<StrategicPillarRaw>): PostObjectResponse<StrategicPillarRaw> {
    if (post.updatedData) {
      post.updatedData = Object.assign(new StrategicPillarRaw(), post.updatedData);
    }
    return post;
  }
}

@Injectable({
  providedIn: 'root'
})
export class StrategicPillarService {
  private apiUrl = '/api/strategic-pillars';
  private refreshSubject = new Subject<void>();
  
  refresh$ = this.refreshSubject.asObservable();

  constructor(private http: HttpClient) {}

  getAllPillars(): Observable<StrategicPillar[]> {
    return this.http.get<StrategicPillar[]>(this.apiUrl).pipe(
      catchError(error => {
        console.error('getStrategicPillars failed:', error.message);
        return of([]);
      })
    );
  }

  getPillarById(id: number): Observable<StrategicPillar> {
    return this.http.get<StrategicPillar>(`${this.apiUrl}/${id}`);
  }

  createPillar(pillar: Omit<StrategicPillar, 'id' | 'created_at' | 'updated_at'>): Observable<StrategicPillar> {
    return this.http.post<StrategicPillar>(this.apiUrl, pillar);
  }

  updatePillar(id: number, pillar: Partial<StrategicPillar>): Observable<StrategicPillar> {
    return this.http.patch<StrategicPillar>(`${this.apiUrl}/${id}`, pillar);
  }

  deletePillar(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  triggerRefresh(): void {
    this.refreshSubject.next();
  }

  // Strategic Pillar Tagging Methods
  tagStrategicPillar(entityType: EntityType, entityId: string, pillarId: number): Observable<{success: boolean}> {
    console.log(`Tagging pillar ${pillarId} to ${entityType} ${entityId}`);
    return this.http.post<{success: boolean}>(`${this.apiUrl}/tag`, {
      entityType,
      entityId,
      pillarId
    });
  }

  untagStrategicPillar(entityType: EntityType, entityId: string, pillarId: number): Observable<{success: boolean}> {
    console.log(`Untagging pillar ${pillarId} from ${entityType} ${entityId}`);
    return this.http.post<{success: boolean}>(`${this.apiUrl}/untag`, {
      entityType,
      entityId,
      pillarId
    });
  }

  // Get strategic pillars for a specific entity
  getTaggedPillars(entityType: EntityType, entityId: string): Observable<StrategicPillar[]> {
    console.log(`Getting tagged pillars for ${entityType} ${entityId}`);
    return this.http.get<StrategicPillar[]>(`${this.apiUrl}/tagged/${entityType}/${entityId}`);
  }
  
  // Note: getAllTaggedPillars method was removed as strategic pillars are now included directly in API responses

  // Update strategic pillar assignment for an entity (single pillar per entity)
  // Backend handles removing existing pillars when tagging a new one
  updateStrategicPillarAssignment(entityType: EntityType, entityId: string, pillarId: number | null): Observable<{success: boolean}> {
    console.log(`Updating strategic pillar assignment for ${entityType} ${entityId} to pillar ${pillarId}`);
    
    // If pillarId is provided, tag the new pillar (backend will handle untagging previous ones)
    if (pillarId !== null) {
      return this.tagStrategicPillar(entityType, entityId, pillarId);
    }
    
    // If pillarId is null, we need to untag all pillars for this entity
    // First get the currently tagged pillars, then untag them
    return new Observable(observer => {
      this.getTaggedPillars(entityType, entityId).subscribe({
        next: (taggedPillars) => {
          if (taggedPillars.length === 0) {
            // No pillars to untag
            observer.next({ success: true });
            observer.complete();
            return;
          }
          
          // Untag the first pillar (should only be one for our use case)
          this.untagStrategicPillar(entityType, entityId, taggedPillars[0].id).subscribe({
            next: (result) => {
              observer.next(result);
              observer.complete();
            },
            error: (error) => observer.error(error)
          });
        },
        error: (error) => observer.error(error)
      });
    });
  }

  // Update strategic pillar assignments for an entity (multiple pillars)
  updateMultipleStrategicPillarAssignments(entityType: EntityType, entityId: string, pillarIds: number[]): Observable<{success: boolean}> {
    console.log(`Updating multiple strategic pillar assignments for ${entityType} ${entityId}: [${pillarIds.join(',')}]`);

    return new Observable(observer => {
      this.getTaggedPillars(entityType, entityId).subscribe({
        next: (taggedPillars) => {
          const currentIds = taggedPillars.map(p => p.id);
          const toTag = pillarIds.filter(id => !currentIds.includes(id));
          const toUntag = currentIds.filter(id => !pillarIds.includes(id));

          const ops: Observable<any>[] = [];
          toTag.forEach(id => ops.push(this.tagStrategicPillar(entityType, entityId, id)));
          toUntag.forEach(id => ops.push(this.untagStrategicPillar(entityType, entityId, id)));

          if (ops.length === 0) {
            observer.next({ success: true });
            observer.complete();
            return;
          }

          forkJoin(ops).subscribe({
            next: (results) => {
              const ok = Array.isArray(results) ? results.every((r: any) => r && r.success !== false) : !!results;
              observer.next({ success: ok });
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        },
        error: (err) => observer.error(err)
      });
    });
  }
}